"use client";

import React, {useEffect, useState} from "react";
import {PagePayoutSchema} from "@/sanity/pages/PagePayoutTemplate";

import {cn} from "@/lib/utils";
import Image from "next/image";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTAButton} from "@/shadcn/ui/button";
import {Carousel, CarouselContent, CarouselItem, type CarouselApi} from "@/shadcn/ui/carousel";

import laurels from "@/public/payout/laurels.svg";
import ipad from "@/public/global/ipad.png";

export default function IntroSection({
  data,
  className
}: {
  data: PagePayoutSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const isBrazil = data?.settings?.slug?.current?.endsWith("/br");

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section className={cn(
      "relative overflow-clip",
      className
    )}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block"
            )}
          />
        ))}
      </div>

      <div className="container flex flex-col items-center pt-[5rem] lg:pt-[7rem] relative">
        {data?.introItems && (
          <div className="bg-white w-[min(180rem,calc(100%-4rem))] border border-[#E5E5E5] px-[2rem] lg:px-[7rem] py-[4rem] lg:py-[5rem] flex flex-col lg:flex-row items-center lg:justify-between gap-[5rem]">
            {data.introItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-[1.75rem]"
              >
                <Image
                  className="!w-[4.125rem] flex-shrink-0"
                  src={laurels}
                  alt="laurels"
                />

                <div className="flex flex-col items-center gap-[.375rem]">
                  <p className="mb-0 text-[#103037] font-semibold font-instrument-sans text-[3.25rem] leading-none">
                    {item?.number?.numberPrefix && (
                      <span>{item.number.numberPrefix}</span>
                    )}
                    {item?.number?.number && (
                      <span>{item.number.number}</span>
                    )}
                    {item?.number?.numberSuffix && (
                      <span>{item.number.numberSuffix}</span>
                    )}
                  </p>
                  <p className="mb-0 text-[#414141] text-[2rem] leading-none">{item?.text}</p>
                </div>

                <Image
                  className="!w-[4.125rem] flex-shrink-0 -scale-x-100"
                  src={laurels}
                  alt="laurels"
                />
              </div>
            ))}
          </div>
        )}

        <div className="bg-white w-[calc(100%-4rem)] sm:w-[min(70rem,100%)] lg:w-[min(84.75rem,100%)] border border-[#E5E5E5] flex flex-col items-center pt-[5rem] pb-[6rem] lg:pt-[9rem] lg:pb-[5rem] mt-[6rem] lg:mt-[12rem] px-[2rem]">
          {data?.introTitle && (
            <div className="richtext sm:w-[45rem] lg:w-[min(55rem,100%)]">
              <PortableText
                value={data.introTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      </div>

      {data?.introImages && (
        <Carousel
          className="w-full -mt-[2rem] [&_>_div]:!overflow-visible"
          setApi={setApi}
          opts={{
            align: "center",
            loop: true,
          }}
        >
          <CarouselContent className="flex [&_>_div]:!overflow-visible">
            {data?.introImages.map((item, index) => (
              <div
                key={index}
                className="flex-shrink-0 basis-[45rem] sm:basis-[60rem] lg:basis-[70rem] px-[1rem]"
              >
                <div className={cn(
                  "p-[1.5rem] sm:p-[2rem] relative transition-transform",
                  current === index ? "scale-100" : "scale-95"
                )}>
                  <Image
                    className={cn(
                      "absolute left-0 top-0 !w-full !h-full rounded-[2.75rem]",
                      current === index && "shadow-[0_20px_25px_7px_rgba(16,48,55,.5)]"
                    )}
                    src={ipad}
                    alt="ipad"
                  />

                  {item?.image && (
                    <Image
                      className="!w-full relative"
                      src={item.image}
                      quality={100}
                      alt=""
                    />
                  )}
                </div>
              </div>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      <div className="container flex flex-col items-center pb-[12rem] lg:pb-[15rem] mt-[4rem] relative">
        {isBrazil && data?.introText && (
          <div className="flex flex-col items-center gap-[3rem] lg:gap-[5.5rem]">
            <div className="richtext w-[min(46.375rem,100%)]">
              <PortableText
                value={data.introText}
                components={richTextComponent}
              />
            </div>
            
            <div className="flex flex-row items-center gap-[1.5rem] lg:gap-[2rem] bg-[#103037] rounded-[5rem] py-[2.5rem] lg:py-[2rem] px-[4rem] lg:px-[7rem]">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="4.88281" y="4.87769" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="4.88281" y="24.3918" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="9.75439" y="9.75708" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="9.75439" y="19.5142" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="9.75439" y="4.87769" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="9.75439" y="24.3918" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="14.6372" y="9.75708" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="14.6372" y="19.5142" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="14.6372" y="14.6348" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="14.6372" y="4.87769" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="14.6372" y="24.3918" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="19.5088" y="9.75708" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="19.5088" y="19.5142" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="19.5088" y="14.6348" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
                <rect x="24.3906" y="14.6348" width="2.43913" height="2.43913" rx="0.487827" fill="#6DA991"/>
              </svg>
              <span className="text-[2rem] text-center lg:text-[2.25rem] font-[500] leading-[120%] text-white">Você pode sacar via transferência bancária, cripto ou USDT/USDC.</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 26.8293 4.87769)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 26.8293 24.3918)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 21.9577 9.75708)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 21.9577 19.5142)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 21.9577 4.87769)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 21.9577 24.3918)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 17.0749 9.75708)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 17.0749 19.5142)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 17.0749 14.6348)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 17.0749 4.87769)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 17.0749 24.3918)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 12.2033 9.75708)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 12.2033 19.5142)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 12.2033 14.6348)" fill="#6DA991" />
                <rect width="2.43913" height="2.43913" rx="0.487827" transform="matrix(-1 0 0 1 7.32146 14.6348)" fill="#6DA991" />
              </svg>
            </div>
          </div>
        )}

        

        {data?.introCTA && (
          <CTAButton
            className="mt-[3rem]"
            data={data.introCTA}
          />
        )}
      </div>
    </section>
  );
}
